/**
 * Notification Emails Card Component
 * Manage email addresses for notifications
 */

import { useState } from 'react';
import { Mail, Plus, Trash2, BadgeCheck, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { NotificationEmail } from '../types';

interface NotificationEmailsCardProps {
  emails: NotificationEmail[];
  isLoading: boolean;
  onAddEmail: (email: string) => Promise<void>;
  onRemoveEmail: (emailId: string) => Promise<void>;
}

export function NotificationEmailsCard({
  emails,
  isLoading,
  onAddEmail,
  onRemoveEmail,
}: NotificationEmailsCardProps) {
  const { t } = useLanguage();
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = async () => {
    setEmailError(null);

    if (!newEmail.trim()) {
      setEmailError(t('notifications.emails.errorEmpty', 'Please enter an email address'));
      return;
    }

    if (!validateEmail(newEmail)) {
      setEmailError(t('notifications.emails.errorInvalid', 'Please enter a valid email address'));
      return;
    }

    if (emails.some(e => e.email.toLowerCase() === newEmail.toLowerCase())) {
      setEmailError(t('notifications.emails.errorDuplicate', 'This email is already added'));
      return;
    }

    setIsAdding(true);
    try {
      await onAddEmail(newEmail);
      setNewEmail('');
    } catch {
      setEmailError(t('notifications.emails.errorAdd', 'Failed to add email'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveEmail = async (emailId: string) => {
    setRemovingId(emailId);
    try {
      await onRemoveEmail(emailId);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('notifications.emails.title', 'Notification Emails')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('notifications.emails.subtitle', 'Where we send your alerts')}
            </p>
          </div>
        </div>
      </div>

      {/* Email list */}
      <div className="p-6 space-y-4">
        {emails.length > 0 && (
          <div className="space-y-3">
            {emails.map(email => (
              <div
                key={email.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <Mail className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{email.email}</span>
                      {email.isPrimary && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                          <BadgeCheck className="w-3 h-3" />
                          {t('notifications.emails.primary', 'Primary')}
                        </span>
                      )}
                    </div>
                    {email.verified && (
                      <span className="text-xs text-green-600">
                        {t('notifications.emails.verified', 'Verified')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Only allow removing non-primary emails */}
                {!email.isPrimary && (
                  <button
                    onClick={() => handleRemoveEmail(email.id)}
                    disabled={removingId === email.id}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title={t('notifications.emails.remove', 'Remove email')}
                  >
                    {removingId === email.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add email input */}
        <div className="pt-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('notifications.emails.addLabel', 'Add another email')}
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="email"
                value={newEmail}
                onChange={e => {
                  setNewEmail(e.target.value);
                  setEmailError(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleAddEmail();
                  }
                }}
                placeholder={t('notifications.emails.placeholder', 'email@example.com')}
                className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={isLoading || isAdding}
              />
            </div>
            <button
              onClick={handleAddEmail}
              disabled={isLoading || isAdding || !newEmail.trim()}
              className="px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isAdding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {t('notifications.emails.addButton', 'Add')}
              </span>
            </button>
          </div>
          {emailError && (
            <p className="mt-2 text-sm text-red-600">{emailError}</p>
          )}
        </div>
      </div>
    </div>
  );
}
